import React, { useState } from "react";
import { Modal } from "../../shared/Modal";
import { Input } from "../../shared/Input";
import { FormActions } from "../../shared/FormActions";
import { Icons } from "../../shared/Icons";
import { companyApi } from "../../../services";
import { useToaster } from "../../shared/Toaster";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToast } = useToaster();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Senha atual é obrigatória";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "Nova senha é obrigatória";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "A senha deve ter pelo menos 6 caracteres";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirmação de senha é obrigatória";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = "A nova senha deve ser diferente da senha atual";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await companyApi.updatePassword({
        oldPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (response.success) {
        showToast("success", "Senha alterada com sucesso!");
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setErrors({});
        onClose();
      } else {
        showToast("error", response.error || "Falha ao alterar senha");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      showToast("error", "Erro ao alterar senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Alterar Senha"
      icon={<Icons.Lock sx={{ fontSize: 24 }} />}
      size="md"
      headerGradient="blue"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Input
            type="password"
            label="Senha Atual"
            value={formData.currentPassword}
            onChange={(e) =>
              handleInputChange("currentPassword", e.target.value)
            }
            placeholder="Digite sua senha atual"
            required
            error={errors.currentPassword}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Input
            type="password"
            label="Nova Senha"
            value={formData.newPassword}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
            placeholder="Digite sua nova senha"
            required
            error={errors.newPassword}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Input
            type="password"
            label="Confirmar Nova Senha"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            placeholder="Confirme sua nova senha"
            required
            error={errors.confirmPassword}
            disabled={loading}
          />
        </div>

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          submitText="Alterar Senha"
          cancelText="Cancelar"
          submitIcon={<Icons.Lock sx={{ fontSize: 18 }} />}
          submitVariant="primary"
        />
      </div>
    </Modal>
  );
};
